import { Info, X } from "lucide-react";
import type { PortalNotice } from "@shared-core/data/customer-api-types";
import { Button } from "../../../app/components/primitives/button";
import { cn } from "../../../app/components/primitives/utils";

/** The top bar notices: above the header on every page, one line each, in the theme's main colour. Plain text. */
export function TopBarNotices({
  notices,
  className,
}: {
  notices: PortalNotice[];
  /** The width of the content they line up with. */
  className?: string;
}) {
  const shown = notices.filter((n) => n.kind === "top_bar");
  if (shown.length === 0) return null;
  return (
    <div
      className="bg-primary text-primary-foreground"
      role="region"
      aria-label="Notices"
    >
      {shown.map((notice) => (
        <p
          key={notice.id}
          className={cn(
            "mx-auto flex items-start gap-2 px-4 py-2 text-sm sm:px-6",
            className ?? "max-w-5xl",
          )}
        >
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>{notice.text}</span>
        </p>
      ))}
    </div>
  );
}

/**
 * The pop-up notice: the newest one showing, until the person closes it. Closing calls `onDismiss`, which remembers
 * it for the person where the read named a dismiss URL.
 */
export function PopUpNotice({
  notices,
  onDismiss,
  contained = false,
}: {
  notices: PortalNotice[];
  onDismiss: (notice: PortalNotice) => void;
  /** Inside the staff preview: sit within the preview frame rather than over the whole screen. */
  contained?: boolean;
}) {
  const notice = notices.find((n) => n.kind === "pop_up");
  if (!notice) return null;
  return (
    <div
      className={
        contained
          ? "absolute inset-0 z-20 flex items-center justify-center bg-black/40 p-4"
          : "fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      }
      role="dialog"
      aria-modal={contained ? undefined : true}
      aria-labelledby={`notice-${notice.id}`}
    >
      <div className="w-full max-w-md rounded-xl bg-card p-6 text-card-foreground shadow-lg">
        <div className="flex items-start justify-between gap-4">
          <h2 id={`notice-${notice.id}`} className="text-base font-semibold">
            Notice
          </h2>
          <button
            type="button"
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
            onClick={() => onDismiss(notice)}
            aria-label="Close notice"
          >
            <X className="size-4" />
          </button>
        </div>
        <p className="mt-3 text-sm">{notice.text}</p>
        <div className="mt-5 flex justify-end">
          <Button onClick={() => onDismiss(notice)}>Got it</Button>
        </div>
      </div>
    </div>
  );
}
